import React from 'react';

import { useSnackbar } from 'notistack';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Box, IconButton, Stack, Tooltip, Typography, type TypographyProps } from '@mui/material';

import { copyToClipboard } from '~/utils/clipboard';

type Props = {
  value: string;
  /** What the identifier is, e.g. "Variant ID". Names it in the copy button and the snackbar. */
  name: string;
  /** Render the name before the value. Off where a column heading or field label already names it. */
  showName?: boolean;
  variant?: TypographyProps['variant'];
  color?: TypographyProps['color'];
};

/** An identifier in monospace with a button that copies it to the clipboard. */
export default function CopyableId({ value, name, showName = false, variant = 'body2', color }: Props) {
  const { enqueueSnackbar } = useSnackbar();

  const handleCopy = async () => {
    const copied = await copyToClipboard(value);
    enqueueSnackbar(copied ? `Copied ${name}` : `Could not copy ${name}`, { variant: copied ? 'success' : 'error' });
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} sx={{ minWidth: 0 }}>
      <Typography variant={variant} color={color} sx={{ minWidth: 0 }}>
        {showName ? `${name}: ` : null}
        <Box component="span" sx={{ fontFamily: 'monospace' }}>
          {value}
        </Box>
      </Typography>
      <Tooltip title={`Copy ${name}`}>
        <IconButton size="small" aria-label={`Copy ${name}`} onClick={handleCopy} sx={{ p: 0.25 }}>
          <ContentCopyIcon sx={{ fontSize: 14 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}
